import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings2, Share2, Users } from 'lucide-react';
import { useToast } from '../../store/ToastContext';
import { QRCodeModal } from './modals/QRCodeModal';
import type { Tab } from '../../types';

interface TabHeaderProps {
    tab: Tab | null;
    memberCount?: number;
    onBack?: () => void;
    onSettings: () => void;
    onMembersClick?: () => void;
}

export const TabHeader: React.FC<TabHeaderProps> = ({
    tab,
    memberCount,
    onBack,
    onSettings,
    onMembersClick
}) => {
    // ... (rest of component)


    const navigate = useNavigate();
    const { showToast } = useToast();
    const [isQRCodeOpen, setIsQRCodeOpen] = useState(false);

    const handleShare = () => {
        setIsQRCodeOpen(true);
    };

    const url = tab ? `${window.location.origin}/join/${tab.id}` : '';

    const handleCopyLink = () => {
        void navigator.clipboard.writeText(url);
        showToast("Join link copied!", "success");
    };

    const handleNativeShare = async () => {
        if (typeof navigator.share === 'function') {
            try {
                await navigator.share({
                    title: `Join Evenly Tab: ${tab?.name ?? ''}`,
                    text: `Split the bill with me on Evenly!`,
                    url: url
                });
            } catch (err) {
                console.error("Error sharing:", err);
            }
        } else {
            handleCopyLink();
        }
    };

    return (
        <>
            <>
                <div className="fixed top-0 left-0 right-0 z-40 bg-bg-card border-b border-border-default shadow-sm">
                    <div className="max-w-md mx-auto flex items-center justify-between p-4">
                        <button onClick={onBack ?? (() => navigate('/'))} aria-label="Back" className="rounded-full p-2 hover:bg-bg-card-alt transition-colors">
                            <ArrowLeft size={24} className="text-text-muted" />
                        </button>
                        <div className="text-center">
                            <h2 className="text-xl font-bold text-text-heading">{tab ? tab.name : 'Loading...'}</h2>
                            {tab && (
                                <div className="flex items-center justify-center gap-2">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-text-faint">
                                        {new Date(tab.date).toLocaleDateString()}
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-1">
                            <button
                                onClick={onMembersClick}
                                disabled={!tab}
                                className="relative rounded-full p-2 hover:bg-bg-card-alt transition-colors group disabled:opacity-50"
                                title="View Members"
                            >
                                <Users size={20} className="text-text-muted group-hover:text-primary-600 transition-colors" />
                                {memberCount !== undefined && memberCount > 0 && (
                                    <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary-600 text-[9px] font-bold text-white shadow-sm ring-2 ring-white dark:ring-slate-900">
                                        {memberCount}
                                    </span>
                                )}
                            </button>
                            <button onClick={onSettings} aria-label="Tab Settings" disabled={!tab} className="rounded-full p-2 hover:bg-bg-card-alt transition-colors disabled:opacity-50">
                                <Settings2 size={20} className="text-text-muted" />
                            </button>
                            <button
                                onClick={handleShare}
                                aria-label="Share Tab"
                                disabled={!tab}
                                className="rounded-full p-2 hover:bg-bg-card-alt transition-colors disabled:opacity-50"
                            >
                                <Share2 size={20} className="text-text-muted" />
                            </button>
                        </div>
                    </div>
                </div>
                <div className="h-[88px] w-full" />
            </>

            {tab && (
                <QRCodeModal
                    isOpen={isQRCodeOpen}
                    onClose={() => { setIsQRCodeOpen(false); }}
                    url={url}
                    tabName={tab.name}
                    onCopy={handleCopyLink}
                    onShare={handleNativeShare}
                />
            )}
        </>
    );
};
